
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, TextField, InputAdornment } from '@mui/material';
import { updateLlpApi } from '../apiServices';
import { PageLoader } from '../../../shared_elements';
import { checkApiStatus, numberWithCommas, roundAmount } from '../../../utils';
import { regexConstants } from '../../../constants/regEx';
import {  globalPutService } from '../../../utils/globalApiServices';
import { trackActivity } from '../../../utils/mixpanel';

const LLPEditModal = ({ modeType, editLLP, shopVisit, onToggleModal, getResponseBack, llpList, updateShopVisitLlpCost }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [isLoading, setLoading] = useState({ skeletonLoader: false, pageLoader: false, buttonLoader: false });
  const [llpData, setLlpData] = useState(editLLP?.data)
  const [error, setError] = useState({});
  const onFieldChange = (key, value) => { setLlpData({ ...llpData, [key]: value }) }
  const onFocusChange = (key) => { setError({ ...error, [key]: "" }) }
  const onEditLLP = async () => {
    let validationInputs = {
      cycles_remaining: (llpData?.cycles_remaining === null ||llpData?.cycles_remaining === undefined || llpData?.cycles_remaining === "")  ? "Please enter Cycles Remaining" : "",
      claim_amount: (llpData?.claim_amount === null ||llpData?.claim_amount === undefined || llpData?.claim_amount === "") ? "Please enter Claim Amount" : (llpData?.sv_amount && Number(llpData?.claim_amount) > Number(llpData?.sv_amount)) ? "Claim Amount cannot be greater than SV Cost" : '',
    }
    if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === '' })) {
      const newTotalCost = (llpList || []).reduce((sum, item) => sum + (item?.id === llpData?.id ? (Number(llpData?.sv_amount) || 0) : (Number(item?.sv_amount) || 0)), 0);
      const hasPrsv = shopVisit?.events?.some((ev) => ev?.maintenance_program?.is_llp_event === false);
      const prsvTotal = Number(shopVisit?.events?.find((ev) => ev?.maintenance_program?.is_llp_event === false)?.cost) || 0;
      const svCost = Number(shopVisit?.cost) || 0;
      if (newTotalCost > svCost || prsvTotal > svCost || (newTotalCost + prsvTotal) > svCost) {
        const parts = [`LLP: ${newTotalCost}`];
        if (hasPrsv) parts.push(`PRSV: ${prsvTotal}`);
        if (hasPrsv) parts.push(`Total: ${newTotalCost + prsvTotal}`);
        parts.push(`Max: ${svCost}`);
        enqueueSnackbar(`Cost exceeds shop visit limit (${parts.join(', ')}).`, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
        return;
      }
      setLoading({ buttonLoader: true })
      const svSyncResult = await updateShopVisitLlpCost(shopVisit, newTotalCost);
      if (!svSyncResult) {
        setLoading({ buttonLoader: false });
        return;
      }
      globalPutService(updateLlpApi(shopVisit?.asset_id), {
        id: llpData?.id,
        cycles_remaining: llpData?.cycles_remaining ? Number(llpData?.cycles_remaining) : 0,
        sv_amount: llpData?.sv_amount ? Number(llpData?.sv_amount) : 0,
        claim_amount:  llpData?.claim_amount ? Number(llpData?.claim_amount) : 0
      }).then(async (response) => {
        if (checkApiStatus(response)) {
          setLoading({ buttonLoader: true })
          await getResponseBack();
          setLoading({ buttonLoader: false })
          onToggleModal()
          trackActivity('Item Edited', { page_title: `Edit Shop Visit LLP`, success_msg: response?.data?.message, item_id: llpData?.id, payload: llpData, response: response?.data })
        } else {
          setLoading({ buttonLoader: false, });
          enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
          trackActivity('Item Edited', { page_title: `Edit Shop Visit LLP`, error_msg: response?.data?.message, item_id: llpData?.id, payload: llpData, response: response?.data })
        }
      });
    } else {
      setError(validationInputs)
      trackActivity(`Item Edited`, { page_title: `Edit Shop Visit LLP`, error_msg: validationInputs, })

    }
  }
const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `Shop Visit Edit LLP`, page_type: page_type, })
    }
  return (
    <>
      <Dialog
        aria-labelledby="customized-dialog-title"
        className='add-lease-dialog'
        maxWidth='md'
        fullWidth
        open={editLLP?.modal}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header'>{'Edit LLP'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                required
                id="cycles_remaining"
                value={modeType === 'view' && llpData?.cycles_remaining !== '' && llpData?.cycles_remaining !== null && llpData?.cycles_remaining !== undefined ? numberWithCommas(roundAmount(llpData?.cycles_remaining)) : llpData?.cycles_remaining}
                onChange={(e, value) => { regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange("cycles_remaining", e.target.value,) : e.preventDefault() }}
                label="Cycles Remaining"
                variant="outlined"
                size="small"
                margin="normal"
                error={!!error?.cycles_remaining}
                helperText={error?.cycles_remaining}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 } }}
                onFocus={() => onFocusChange("cycles_remaining")}
                fullWidth
                disabled={modeType === 'view'}
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                id="sv_amount"
                value={modeType === 'view' && llpData?.sv_amount !== '' && llpData?.sv_amount !== null && llpData?.sv_amount !== undefined ? numberWithCommas(roundAmount(llpData?.sv_amount)) : llpData?.sv_amount}
                onChange={(e, value) => { regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange("sv_amount", e.target.value,) : e.preventDefault() }}
                label="SV Cost"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                onFocus={() => onFocusChange("sv_amount")}
                fullWidth
                disabled={modeType === 'view' || shopVisit?.status?.value === 3}
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                required
                id="claim_amount"
                value={modeType === 'view' && llpData?.claim_amount !== '' && llpData?.claim_amount !== null && llpData?.claim_amount !== undefined ? numberWithCommas(roundAmount(llpData?.claim_amount)) : llpData?.claim_amount}
                onChange={(e, value) => { regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange("claim_amount", e.target.value,) : e.preventDefault() }}
                label="Claim Amount"
                variant="outlined"
                size="small"
                margin="normal"
                error={!!error?.claim_amount}
                helperText={error?.claim_amount}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 }, input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                onFocus={() => onFocusChange("claim_amount")}
                fullWidth
                disabled={modeType === 'view'}
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                id="part_number"
                value={llpData?.part_number}
                label="Part Number"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 } }}
                fullWidth
                disabled={true}
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                id="serial_number"
                value={llpData?.serial_number ? llpData?.serial_number : ""}
                label="Serial Number"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 } }}
                fullWidth
                disabled={true}
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                id="module"
                value={llpData?.module}
                label="Module"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 } }}
                fullWidth
                rows={2}
                disabled={true}
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                id="description"
                value={llpData?.description}
                label="Description"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 } }}
                fullWidth
                disabled={true}
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                id="csn"
                value={llpData?.csn}
                label="CSN"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 } }}
                fullWidth
                disabled={true}
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <TextField
                id="cycles_max_limit"
                value={llpData?.cycles_max_limit}
                label="Cycle Max Limit"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 255 } }}
                fullWidth
                disabled={true}
              />
            </Grid>
            <Grid size={{ md: 12, xs: 12 }}>
              <TextField
                id="remarks"
                value={llpData?.remarks}
                label="Remarks"
                variant="outlined"
                size="small"
                margin="normal"
                slotProps={{ inputLabel: { shrink: true }}}
                fullWidth
                rows={3}
                multiline
                disabled={true}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' size='small' disabled={isLoading?.buttonLoader} onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}> Cancel </Button>
          <Button variant='contained' className='btn-primary' disabled={modeType === 'view'} size='small' onClick={() => { handleItemClicked('Save Button'); onEditLLP() }}>
            {isLoading?.buttonLoader ? <CircularProgress color='inherit' size={25} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      {isLoading?.pageLoader ? <PageLoader /> : null}
    </>

  )
}

export default LLPEditModal